/**
 * DeleteAccountDialog — in-app account-deletion flow required by:
 *   - Apple App Store guideline 5.1.1(v)
 *   - Google Play "User Data" policy
 *   - GDPR Article 17 (Right to Erasure)
 *
 * Password-based accounts must re-enter their password to confirm.
 * OAuth accounts (google, etc.) must retype their exact email as a
 * sanity check. Deletion is permanent and irreversible; every
 * user-scoped row across ~30 collections is purged server-side.
 */
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, UserX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const WILL_BE_DELETED = [
  "All portfolios, saved backtests and scenarios",
  "Broker connections (SnapTrade link is revoked)",
  "Watchlists, alerts and push subscriptions",
  "AI chat history and research explanations",
  "Newsletter subscription and analytics events",
  "Your account record itself — this cannot be undone",
];

export default function DeleteAccountDialog({ open, onOpenChange, user, onDeleted }) {
  const isPassword = (user?.auth_provider || "password").toLowerCase() === "password";
  const [password, setPassword] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setPassword(""); setEmailConfirm(""); setReason("");
    setConfirmChecked(false); setError(""); setSubmitting(false);
  };

  const canSubmit = confirmChecked && !submitting && (
    isPassword ? password.length >= 1 : emailConfirm.trim().toLowerCase() === (user?.email || "").toLowerCase()
  );

  const handleDelete = async () => {
    setSubmitting(true);
    setError("");
    try {
      const body = isPassword
        ? { password, reason: reason.trim() || undefined }
        : { confirm_email: emailConfirm.trim(), reason: reason.trim() || undefined };
      const res = await api.delete("/auth/account", { data: body });
      toast.success("Your account has been permanently deleted.");
      reset();
      onOpenChange(false);
      // Delay onDeleted slightly so the toast is visible during the redirect.
      setTimeout(() => onDeleted?.(res.data), 400);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Deletion failed";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-xl inline-flex items-center gap-2 text-destructive">
            <UserX className="w-5 h-5" /> Delete your account
          </DialogTitle>
          <DialogDescription>
            This action is <b>permanent</b> and takes effect immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-destructive/25 bg-destructive/[0.04] p-3">
          <div className="text-xs mono uppercase tracking-widest text-destructive mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> What will be deleted
          </div>
          <ul className="text-xs text-foreground/80 space-y-1 list-disc list-inside">
            {WILL_BE_DELETED.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>

        {isPassword ? (
          <div>
            <Label htmlFor="del-password">Enter your current password</Label>
            <Input
              id="del-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setError(""); setPassword(e.target.value); }}
              disabled={submitting}
              data-testid="delete-account-password"
              placeholder="Password"
            />
          </div>
        ) : (
          <div>
            <Label htmlFor="del-email">Retype your email to confirm</Label>
            <Input
              id="del-email"
              type="email"
              value={emailConfirm}
              onChange={(e) => { setError(""); setEmailConfirm(e.target.value); }}
              disabled={submitting}
              data-testid="delete-account-email-confirm"
              placeholder={user?.email || "you@example.com"}
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Must exactly match <span className="mono">{user?.email}</span>
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="del-reason">Reason (optional — helps us improve)</Label>
          <Input
            id="del-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            maxLength={200}
            data-testid="delete-account-reason"
            placeholder="Anything you'd like us to know?"
          />
        </div>

        <label className="flex items-start gap-2 text-xs text-foreground/80 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.target.checked)}
            disabled={submitting}
            data-testid="delete-account-confirm-check"
            className="mt-0.5 h-4 w-4 accent-destructive"
          />
          <span>I understand this deletes my account and all associated data permanently. This action cannot be undone.</span>
        </label>

        {error && (
          <div className="text-xs text-destructive" role="alert" data-testid="delete-account-error">
            {error}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="delete-account-cancel">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canSubmit}
            data-testid="delete-account-confirm"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Deleting…</>) : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
